/*
 * prarvi-analytics.js — single forwarding layer for prarvi:* events.
 *
 * Exposes window.Prarvi.track(name, detail) as the canonical entry point.
 * Every prarvi event source (concierge-capture.js, ad-video-promo.js,
 * chatbot-widget.js) prefers Prarvi.track when available and falls back
 * to dispatchEvent if not. Result: one click → one analytics call,
 * regardless of script load order.
 *
 * Forwarding:
 *   - window.dataLayer push (GTM)
 *   - gtag("event", ...) if gtag is present
 *   - fbq("trackCustom", ...) if fbq is present
 *   - window.dispatchEvent(new CustomEvent("prarvi:" + name, { detail }))
 *     so any external listeners (tag managers using addEventListener,
 *     debug overlays, etc.) still see the event exactly once.
 *
 * No window.dispatchEvent monkey-patch (P3-003).
 *
 * Also: global click delegate for elements with data-prarvi-event.
 * Each click fires exactly once via Prarvi.track.
 */
(function () {
  'use strict';

  var PREFIX = 'prarvi:';

  // Events that are genuine lead submissions -> fire a STANDARD Meta "Lead"
  // (so Meta can build a Lead custom conversion + optimize for it). Value is the
  // content_category. Any event also fires Lead if its detail has conversion:'true'.
  // We deliberately do NOT trackCustom every event into the pixel — that would
  // flood Meta with one custom event per click; non-lead events stay on the
  // dataLayer / Shopify customer-events bus only.
  var LEAD_EVENTS = {
    b2b_lead: 'wholesale_b2b',
    contact_form_submit: 'contact',
    photo_match: 'photo_match',
    research_lead: 'lab_research',
    popup_lead: 'retail',
  };
  var firedLeads = {}; // eventID -> true, dedupe per page-load (and Meta dedupes the eventID)

  // Google Ads website "Wholesale Lead Form" conversion. Mirrors the proven
  // self-inject in sections/b2b-lead-form.liquid so EVERY lead form (photo-match,
  // research, contact, popup) fires it — not just the B2B markers. transaction_id
  // dedupes against the B2B handler's fire for the same lead.
  var GADS_ID = 'AW-930409328';
  var GADS_LEAD_SEND = 'AW-930409328/l70wCN3b1rwcEPDW07sD';
  function fireGoogleAdsLead(eid) {
    try {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') { window.gtag = function () { window.dataLayer.push(arguments); }; }
      if (!document.querySelector('script[data-prarvi-aw]')) {
        var s = document.createElement('script');
        s.async = true;
        s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GADS_ID;
        s.setAttribute('data-prarvi-aw', '1');
        document.head.appendChild(s);
        window.gtag('js', new Date());
        window.gtag('config', GADS_ID);
      }
      window.gtag('event', 'conversion', { send_to: GADS_LEAD_SEND, transaction_id: eid });
    } catch (e) { void e; }
  }

  function sanitizeEventName(name) {
    return String(name || '')
      .replace(/^prarvi:/, '')
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .toLowerCase();
  }

  function cloneDetail(detail) {
    var out = {};
    if (!detail || typeof detail !== 'object') return out;
    Object.keys(detail).forEach(function (key) {
      var value = detail[key];
      if (value == null) return;
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        out[key] = value;
      }
    });
    return out;
  }

  function dataAttrs(el) {
    var out = {};
    if (!el || !el.attributes) return out;
    Array.prototype.forEach.call(el.attributes, function (attr) {
      if (!attr || attr.name.indexOf('data-prarvi-') !== 0) return;
      var key = attr.name.replace('data-prarvi-', '').replace(/-/g, '_');
      if (key === 'event') return;
      out[key] = attr.value;
    });
    return out;
  }

  function isCheckoutTarget(el) {
    if (!el || !el.closest) return false;
    var target = el.closest('a[href^="/checkout"], button[name="checkout"], input[name="checkout"]');
    return target || null;
  }

  function forward(name, detail) {
    var eventName = sanitizeEventName(name);
    var params = cloneDetail(detail);
    params.prarvi_event_name = eventName;
    params.prarvi_source_event = PREFIX + eventName;

    // Always push a normalized named-object entry so GTM triggers
    // configured on event == "prarvi_event" fire regardless of whether
    // gtag is also loaded (P1-028). gtag's own internal arguments-array
    // push is separate and doesn't satisfy that trigger pattern.
    window.dataLayer = window.dataLayer || [];
    var entry = { event: 'prarvi_event' };
    Object.keys(params).forEach(function (k) {
      entry[k] = params[k];
    });
    window.dataLayer.push(entry);

    // P1-328 plumbing: publish to Shopify's Customer Events bus so SANDBOXED web pixels
    // (Meta / GA4 added via Settings -> Customer events) can consume theme events. The theme
    // loads no pixel in main scope, so window.gtag/fbq below are usually absent; this bus is
    // the reliable path. Subscribers do: analytics.subscribe('prarvi_event', e => ... e.customData.prarvi_event_name ...).
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('prarvi_event', params);
      }
    } catch (err) {
      void err;
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }

    // Lead submissions -> standard conversions on Meta + Google Ads, once per page.
    var category = LEAD_EVENTS[eventName];
    var isLead = !!category || params.conversion === 'true' || params.conversion === true;
    if (isLead) {
      // Stable per success-page so a reload doesn't double-count; also the Meta
      // eventID so Pixel<->CAPI (server-side) dedupe, and the GAds transaction_id.
      var eid = 'lead-' + eventName + '-' + (String(window.location.pathname).replace(/[^a-z0-9]/gi, '') || 'p');
      if (!firedLeads[eid]) {
        firedLeads[eid] = true;
        // Meta standard Lead (window.fbq from snippets/prarvi-meta-pixel.liquid).
        if (typeof window.fbq === 'function') {
          try {
            window.fbq('track', 'Lead', {
              content_category: category || params.audience || 'lead',
              content_name: eventName,
            }, { eventID: eid });
          } catch (err) { void err; }
        }
        // Google Ads lead conversion. The B2B/free-sample funnel fires its own
        // proven AW conversion in sections/b2b-lead-form.liquid, so skip b2b_lead
        // here to avoid a double; cover every OTHER lead form (photo-match,
        // research, contact, popup) that previously fired no Google conversion.
        if (eventName !== 'b2b_lead') {
          fireGoogleAdsLead(eid);
        }
        // GA4 standard lead conversion. Same gtag path that already records the
        // theme's events into GA4 (G-X8HJM2KPJ4) — gives GA4 ONE canonical
        // 'generate_lead' to mark as a Key Event (instead of N custom names), so
        // AI-sourced leads (ChatGPT/Claude referrals) trend as real conversions.
        if (typeof window.gtag === 'function') {
          try {
            window.gtag('event', 'generate_lead', {
              lead_type: eventName,
              content_category: category || params.audience || 'lead',
            });
          } catch (err) { void err; }
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'generate_lead', lead_type: eventName, content_category: category || params.audience || 'lead' });
      }
    }
  }

  function track(name, detail) {
    var sanitized = sanitizeEventName(name);
    forward(sanitized, detail);
    try {
      window.dispatchEvent(new CustomEvent(PREFIX + sanitized, { detail: detail || {} }));
    } catch (err) {
      // dispatch is best-effort; swallow if CustomEvent is unsupported.
      void err;
    }
  }

  // Dedupe guard for checkout_start: on the cart page a single checkout
  // action can trip both the click and the submit listener. Allow the
  // actual track call at most once per ~1000ms window (P2-178).
  var lastCheckoutStart = 0;

  function trackCheckoutStart(detail) {
    var now = Date.now();
    if (now - lastCheckoutStart < 1000) return;
    lastCheckoutStart = now;
    window.Prarvi.track('checkout_start', detail);
  }

  // Public API.
  window.Prarvi = window.Prarvi || {};
  if (!window.Prarvi.track) window.Prarvi.track = track;

  // Global click delegate.
  document.addEventListener('click', function (event) {
    var el = event.target && event.target.closest ? event.target.closest('[data-prarvi-event]') : null;
    if (!el) return;

    var name = el.getAttribute('data-prarvi-event');
    if (!name) return;

    var detail = dataAttrs(el);
    detail.label = detail.label || el.getAttribute('data-prarvi-label') || (el.textContent || '').trim();
    detail.href = detail.href || el.getAttribute('href') || '';
    detail.audience = detail.audience || el.getAttribute('data-prarvi-audience') || '';
    detail.placement = detail.placement || el.getAttribute('data-prarvi-placement') || '';
    window.Prarvi.track(name, detail);
  });

  // Auto-track tel:/mailto: clicks that don't already carry a data-prarvi-event,
  // so explicitly-named phone/email events stay authoritative and this only fills
  // the gaps (P1-317: ~12 of 20 tel: links were untracked). Fires once per click.
  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest
      ? event.target.closest('a[href^="tel:"], a[href^="mailto:"]')
      : null;
    if (!link || link.closest('[data-prarvi-event]')) return;
    var href = link.getAttribute('href') || '';
    var isPhone = href.indexOf('tel:') === 0;
    window.Prarvi.track(isPhone ? 'phone_click' : 'email_click', {
      href: href,
      label: (link.textContent || '').trim(),
      audience: link.getAttribute('data-prarvi-audience') || 'retail',
      placement: 'auto_link',
    });
  });

  document.addEventListener('click', function (event) {
    var target = isCheckoutTarget(event.target);
    if (!target || target.closest('[data-prarvi-event]')) return;
    if (target.matches && !target.matches('a[href^="/checkout"]')) return;
    trackCheckoutStart({
      label: (target.textContent || target.value || 'Checkout').trim(),
      href: target.getAttribute('href') || '',
      placement: target.closest('.main-cart') ? 'cart_page' : target.closest('[data-cart-drawer]') ? 'cart_drawer' : 'site',
      audience: 'retail',
    });
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form')) return;
    var submitter = event.submitter || document.activeElement;
    if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
    trackCheckoutStart({
      label: submitter.getAttribute('data-prarvi-label') || (submitter.textContent || submitter.value || 'Checkout').trim(),
      placement: form.closest('.main-cart') ? 'cart_page' : 'cart_form',
      audience: 'retail',
    });
  });

  function shadeMatchDetail(el) {
    return {
      product_title: el.getAttribute('data-product-title') || '',
      product_type: el.getAttribute('data-product-type') || '',
      product_url: el.getAttribute('data-product-url') || '',
      default_open: el.getAttribute('data-default-open') || 'false',
      placement: 'pdp_trust_panel',
      audience: 'retail',
    };
  }

  function trackShadeMatchView(details) {
    if (!details || details.getAttribute('data-view-tracked') === 'true') return;
    details.setAttribute('data-view-tracked', 'true');
    window.Prarvi.track('shade_match_form_view', shadeMatchDetail(details));
  }

  function bindShadeMatchTracking() {
    var shadeDetails = document.querySelectorAll('[data-shade-match-details]');
    shadeDetails.forEach(function (details) {
      if (details.open) trackShadeMatchView(details);
      details.addEventListener('toggle', function () {
        if (details.open) trackShadeMatchView(details);
      });
    });

    document.addEventListener('submit', function (event) {
      var form = event.target && event.target.closest ? event.target.closest('.shade-match-form') : null;
      if (!form) return;
      var details = form.closest('[data-shade-match-details]');
      window.Prarvi.track('shade_match_form_submit', shadeMatchDetail(details || form));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindShadeMatchTracking);
  } else {
    bindShadeMatchTracking();
  }

  // Marked leads: contact forms that reload to a success state with no JS of
  // their own drop a hidden marker in their success block:
  //   <span class="prarvi-lead" data-lead-event="photo_match" data-lead-category="photo_match" hidden></span>
  // Scan once on load and route each through the bus -> standard Meta Lead + GA4.
  function fireMarkedLeads() {
    var marks = document.querySelectorAll('.prarvi-lead');
    Array.prototype.forEach.call(marks, function (m) {
      if (m.getAttribute('data-fired') === '1') return;
      m.setAttribute('data-fired', '1');
      window.Prarvi.track(m.getAttribute('data-lead-event') || 'lead_submit', {
        conversion: 'true',
        content_category: m.getAttribute('data-lead-category') || 'lead',
        audience: m.getAttribute('data-audience') || 'retail',
        placement: m.getAttribute('data-placement') || 'form',
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fireMarkedLeads);
  } else {
    fireMarkedLeads();
  }

  // ── P2-201: scroll-depth tracking (50% + 90%), one-shot each per page ──
  // Guarded to blog/article pages only so we don't add scroll listeners on
  // every template. GA4 Enhanced Measurement covers 90% globally; these custom
  // thresholds give a consistent 50% + 90% signal on long-form blog content.
  function bindScrollDepth() {
    var isArticle = !!document.querySelector('.article-modern__content');
    var onBlogPath = /\/blogs\//.test(window.location.pathname);
    if (!isArticle && !onBlogPath) return;

    var thresholds = [
      { pct: 50, fired: false },
      { pct: 90, fired: false },
    ];
    var ticking = false;

    function evaluate() {
      ticking = false;
      var doc = document.documentElement;
      var scrollTop = window.pageYOffset || doc.scrollTop || 0;
      var viewport = window.innerHeight || doc.clientHeight || 0;
      var full = Math.max(
        document.body ? document.body.scrollHeight : 0,
        doc.scrollHeight || 0,
      );
      var scrollable = full - viewport;
      // Short pages (no scroll room) count as fully read.
      var depth = scrollable <= 0 ? 100 : ((scrollTop + viewport) / full) * 100;

      var allFired = true;
      thresholds.forEach(function (t) {
        if (!t.fired && depth >= t.pct) {
          t.fired = true;
          window.Prarvi.track('scroll_depth', {
            percent: t.pct,
            source: 'blog',
          });
        }
        if (!t.fired) allFired = false;
      });
      if (allFired) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(evaluate);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Evaluate once in case the page loads already past a threshold.
    evaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindScrollDepth);
  } else {
    bindScrollDepth();
  }
})();


/* Attribution capture (P1-328): fill hidden lead-form fields gclid/fbclid/_fbp/_fbc for
   server-side Google offline + Meta CAPI matching. Persists click IDs to first-party cookies
   so they survive navigation from the ad landing page to the form. */
(function () {
  'use strict';
  function param(n) {
    var m = (window.location.search || '').match(new RegExp('[?&]' + n + '=([^&]*)'));
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : '';
  }
  function cookie(n) {
    var parts = ('; ' + document.cookie).split('; ' + n + '=');
    return parts.length === 2 ? decodeURIComponent(parts.pop().split(';').shift()) : '';
  }
  function setCookie(n, v) {
    if (!v) return;
    try {
      document.cookie = n + '=' + encodeURIComponent(v) + '; Max-Age=7776000; Path=/; SameSite=Lax' +
        (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (e) { void e; }
  }
  var gclid = param('gclid'); if (gclid) setCookie('prarvi_gclid', gclid);
  var fbclid = param('fbclid'); if (fbclid) setCookie('prarvi_fbclid', fbclid);
  function valueFor(src) {
    switch (src) {
      case 'gclid': return param('gclid') || cookie('prarvi_gclid');
      case 'fbclid': return param('fbclid') || cookie('prarvi_fbclid');
      case '_fbp': return cookie('_fbp');
      case '_fbc': {
        var fbc = cookie('_fbc');
        if (fbc) return fbc;
        var fid = param('fbclid') || cookie('prarvi_fbclid');
        return fid ? ('fb.1.' + Date.now() + '.' + fid) : '';
      }
      default: return '';
    }
  }
  function fill() {
    var els = document.querySelectorAll('input[data-capture]');
    Array.prototype.forEach.call(els, function (el) {
      if (el.value) return;
      var v = valueFor(el.getAttribute('data-capture'));
      if (v) el.value = v;
    });
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', fill); } else { fill(); }
  document.addEventListener('submit', fill, true);
})();
